// Pure matcher for invoice warning clauses (suspicious phrases operators
// flag on invoices/line items — e.g. intermediary/agency language that
// should block reconciliation until reviewed).

export interface WarningClause {
  id: number;
  phrase: string;
  note: string | null;
  is_active: boolean;
}

/**
 * Fold text to the form clause phrases are compared in.
 *
 * Strips Polish diacritics so text that drops them still matches phrases that
 * don't (and vice versa). NFD splits `ó` into `o` + combining accent which we
 * then drop; `ł` doesn't decompose so we map it explicitly. The
 * diacritic-stripping portion is copied from
 * `shared/services/mbank/matching/tokenizeName.ts` `stripDiacritics` —
 * duplicated rather than imported because that module also strips legal-form
 * suffixes (sp. z o.o., S.A., …), which is wrong for clause matching.
 *
 * This is an ANTI-EVASION control, not just a convenience fold: the text it
 * reads is vendor-supplied (Fakturownia / KSeF), and a match blocks
 * auto-approval. So it also
 *   - drops Unicode format + zero-width characters (`\p{Cf}`, soft hyphen) —
 *     `po<ZWSP>średnictwo` renders identically to a human but would otherwise
 *     sail past `includes()`, silently defeating BOTH the approval block and
 *     the row tint the operator would have seen;
 *   - normalizes NFKC first, so compatibility variants (fullwidth, ligatures)
 *     fold to their plain forms before the diacritic pass.
 *
 * Homoglyph confusables (Cyrillic `о` for Latin `o`) are deliberately NOT
 * handled — that needs a confusables table, and half-solving it here would
 * read as coverage we don't have.
 */
export function normalizeClauseText(s: string): string {
  return s
    .normalize('NFKC')
    .replace(/[­\p{Cf}]/gu, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Returns every active clause whose normalized phrase appears as a
 * substring of any normalized text, case- and diacritic-insensitively.
 * Preserves the input clause order; never duplicates a clause.
 */
export function findWarningClauses(clauses: WarningClause[], texts: (string | null | undefined)[]): WarningClause[] {
  const normalizedTexts = texts.filter((t): t is string => !!t && t.trim() !== '').map(normalizeClauseText);

  return clauses.filter((clause) => {
    if (!clause.is_active) return false;
    const normalizedPhrase = normalizeClauseText(clause.phrase);
    if (!normalizedPhrase) return false;
    return normalizedTexts.some((text) => text.includes(normalizedPhrase));
  });
}
